import { Button, Group, Select, Stack } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useModuleManagementStore } from 'store/moduleManagement';
import { FormDataCreateCompetencyModule } from 'types/dacum/moduleManagement';
import { UseMutationResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { moduleManagementService } from 'services/dacum/module.service';
import Card from 'components/Card';
import { courseService } from 'services/lcms/course.service';
import { Course } from 'types/lcms/courses';
import { dacumService } from 'services/dacum/dacum.service';
import { CompetencyData } from 'types/dacum/competencyManagement';

interface ModuleManagementFormProps {
  createModule: UseMutationResult<any, Error, FormDataCreateCompetencyModule>;
  updateModule: UseMutationResult<any, Error, FormDataCreateCompetencyModule>;
}

const PARAMS = {
  pageSize: 10000,
  pageNumber: 1
};

const ModuleManagementForm = ({ createModule, updateModule }: ModuleManagementFormProps) => {
  const navigate = useNavigate();
  const { formData, mode, setFormData } = useModuleManagementStore();

  const {
    control,
    reset,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<FormDataCreateCompetencyModule>({
    defaultValues: {
      msCompetencyManagementID: formData.msCompetencyManagementID || '',
      msLCMSCourseID: formData.msLCMSCourseID || '',
      deliveryMode: formData.deliveryMode || '',
      liaType: formData.liaType || ''
    },
    mode: 'all',
    reValidateMode: 'onChange'
  });

  // Fetch dropdown data
  const { data: competencyManagementList, isPending: loadingCompetencyManagement } = useQuery({
    queryKey: ['competencyManagementList'],
    queryFn: () => dacumService.getCompetency(PARAMS),
    staleTime: Infinity
  });

  const { data: lcmsCourseList, isPending: loadingLCMSCourse } = useQuery({
    queryKey: ['lcmsCourseList'],
    queryFn: () => courseService.list({ ...PARAMS, pageSize: 10 }),
    staleTime: Infinity
  });

  const { data: modeOfDeliveryList, isPending: loadingModeOfDelivery } = useQuery({
    queryKey: ['modeOfDeliveryList'],
    queryFn: () => courseService.getModeOfDelivery(PARAMS),
    staleTime: Infinity
  });

  const liaTypeOptions = moduleManagementService.getLIATypeOptions();

  useEffect(() => {
    reset({
      msCompetencyManagementID: formData.msCompetencyManagementID || '',
      msLCMSCourseID: formData.msLCMSCourseID || '',
      deliveryMode: formData.deliveryMode || '',
      liaType: formData.liaType || ''
    });
    setValue('msCompetencyManagementID', formData.msCompetencyManagementID || '');
    setValue('msLCMSCourseID', formData.msLCMSCourseID || '');
    setValue('deliveryMode', formData.deliveryMode || '');
    setValue('liaType', formData.liaType || '');
  }, [formData, reset, setValue]);

  const onSubmit = (data: FormDataCreateCompetencyModule) => {
    setFormData(data);

    if (mode === 'add') {
      createModule.mutate(data);
    } else if (mode === 'edit') {
      updateModule.mutate(data);
    }
  };

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={32}>
          <Stack gap={16}>
            <Controller
              name='msCompetencyManagementID'
              control={control}
              rules={{ required: 'Competency Management is required' }}
              render={({ field }) => (
                <Select
                  label='Competency Management'
                  placeholder='Select competency management'
                  data={
                    competencyManagementList?.data?.map((item: CompetencyData) => ({
                      value: item.msCompetencyManagementID,
                      label:
                        item.schoolName || item.keyAction || `ID: ${item.msCompetencyManagementID}`
                    })) || []
                  }
                  searchable
                  disabled={mode === 'view' || loadingCompetencyManagement}
                  error={errors.msCompetencyManagementID?.message}
                  value={field.value}
                  onChange={(value) => field.onChange(value || '')}
                />
              )}
            />

            <Controller
              name='msLCMSCourseID'
              control={control}
              rules={{ required: 'LCMS Course is required' }}
              render={({ field }) => (
                <Select
                  label='LCMS Course'
                  placeholder='Select LCMS course'
                  data={
                    lcmsCourseList?.data?.map((item: Course) => ({
                      value: item.msLCMSCourseID,
                      label: item.courseName || `ID: ${item.msLCMSCourseID}`
                    })) || []
                  }
                  searchable
                  disabled={mode === 'view' || loadingLCMSCourse}
                  error={errors.msLCMSCourseID?.message}
                  value={field.value}
                  onChange={(value) => field.onChange(value || '')}
                />
              )}
            />

            <Controller
              name='deliveryMode'
              control={control}
              rules={{ required: 'Delivery Mode is required' }}
              render={({ field }) => (
                <Select
                  label='Delivery Mode'
                  placeholder='Select delivery mode'
                  data={
                    modeOfDeliveryList?.data?.map((item: any) => ({
                      value: item.option || item.value,
                      label: item.option || item.label
                    })) || []
                  }
                  searchable
                  disabled={mode === 'view' || loadingModeOfDelivery}
                  error={errors.deliveryMode?.message}
                  value={field.value}
                  onChange={(value) => field.onChange(value || '')}
                />
              )}
            />

            <Controller
              name='liaType'
              control={control}
              rules={{ required: 'LIA Type is required' }}
              render={({ field }) => (
                <Select
                  label='LIA Type'
                  placeholder='Select LIA type'
                  data={liaTypeOptions}
                  disabled={mode === 'view'}
                  error={errors.liaType?.message}
                  value={field.value}
                  onChange={(value) => field.onChange(value || '')}
                />
              )}
            />
          </Stack>

          <Group justify='flex-end'>
            <Button variant='outline' onClick={() => navigate(-1)}>
              {mode === 'view' ? 'Back' : 'Cancel'}
            </Button>
            {mode !== 'view' && (
              <Button
                type='submit'
                loading={isSubmitting || createModule.isPending || updateModule.isPending}
                size='md'
                leftSection={<IconDeviceFloppy />}
              >
                {mode === 'add' ? 'Create' : 'Update'} Module
              </Button>
            )}
          </Group>
        </Stack>
      </form>
    </Card>
  );
};

export default ModuleManagementForm;
